import { Link } from 'react-router-dom';
import { FileVideo, Image, Clock, MoreVertical, Trash2, Eye } from 'lucide-react';
import { useState } from 'react';
import { ContentStatusBadge } from '../common/StatusBadge';
import { formatDistanceToNow } from 'date-fns';
import type { ContentItem } from '../../types';

interface ContentGridProps {
  items: ContentItem[];
  onDelete?: (contentId: string) => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function ContentGrid({ items, onDelete }: ContentGridProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item) => (
        <ContentCard key={item.contentId} item={item} onDelete={onDelete} />
      ))}
    </div>
  );
}

interface ContentCardProps {
  item: ContentItem;
  onDelete?: (contentId: string) => void;
}

function ContentCard({ item, onDelete }: ContentCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const isVideo = item.mediaType === 'video';
  const uploadDate = item.uploadedAt ? new Date(item.uploadedAt) : null;
  const timeAgo = uploadDate && !isNaN(uploadDate.getTime())
    ? formatDistanceToNow(uploadDate, { addSuffix: true })
    : '';

  return (
    <div className="card group transition-shadow hover:shadow-md">
      {/* Thumbnail / Preview */}
      <Link to={`/content/${item.contentId}`} className="block">
        <div className="relative aspect-video bg-gray-100">
          {item.thumbnailDownloadUrl ? (
            <img
              src={item.thumbnailDownloadUrl}
              alt={item.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              {isVideo ? (
                <FileVideo className="h-12 w-12 text-gray-300" />
              ) : (
                <Image className="h-12 w-12 text-gray-300" />
              )}
            </div>
          )}

          {/* Duration overlay for videos */}
          {isVideo && item.duration && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
              <Clock className="h-3 w-3" />
              {formatDuration(item.duration)}
            </div>
          )}

          {/* Media type badge */}
          <div className="absolute left-2 top-2">
            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium text-white ${isVideo ? 'bg-purple-600' : 'bg-blue-600'}`}>
              {isVideo ? 'Video' : 'Image'}
            </span>
          </div>
        </div>
      </Link>

      {/* Info */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/content/${item.contentId}`}
            className="min-w-0 flex-1"
          >
            <h3 className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-green">
              {item.title}
            </h3>
          </Link>

          {/* Actions menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 z-20 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <Link
                    to={`/content/${item.contentId}`}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowMenu(false)}
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Link>
                  {onDelete && (
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onDelete(item.contentId);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <ContentStatusBadge status={item.status} />
          <span className="text-xs text-gray-500">{timeAgo}</span>
        </div>

        {item.description && (
          <p className="mt-1.5 line-clamp-2 text-xs text-gray-500">
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
}
